import { useQuery } from '@tanstack/react-query';
import { AccountStateResponseSchema } from '@clay-run/keystone-contract';
import { useApi } from '../lib/api';
import { isMalformedResponse } from '../lib/apiFetchJson';
import { fetchJsonOnHttp } from '../lib/fetchJsonOnHttp';

/**
 * React Query hook for fetching Keystone account intelligence state.
 * Calls GET /api/keystone/:accountId/state via apiFetch.
 *
 * Returns `{ data: null, status: 404|503 }` for expected empty states
 * so the component can render appropriate UI without triggering the
 * global error toast.
 */
/** Root of the Keystone-state cache key — import, never re-type the literal. */
export const KEYSTONE_STATE_KEY = ['keystoneState'] as const;

export function useKeystoneState(accountId: string | null) {
  const { apiFetch } = useApi();

  return useQuery({
    queryKey: [...KEYSTONE_STATE_KEY, accountId],
    queryFn: () =>
      fetchJsonOnHttp(
        apiFetch,
        AccountStateResponseSchema,
        `/api/keystone/${accountId}/state`,
        undefined,
        (err) => {
          // Expected empty states — return sentinel instead of throwing
          if (err.status === 404) return { _empty: 'not_found' as const };
          if (err.status === 503) return { _empty: 'not_configured' as const };
          throw err;
        },
      ),
    enabled: Boolean(accountId),
    staleTime: 0,
    gcTime: 5 * 60_000,
    // A body that failed its schema fails the same way again; only transport errors retry.
    retry: (failureCount, err) => !isMalformedResponse(err) && failureCount < 1,
  });
}
